import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/admin/Layout';
import { FileText, Plus, Edit, Trash2, Globe, Download, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { getCountryTranslations } from '../../translations/countryTranslations';

interface Survey {
  id: string;
  title: string;
  description: string;
  points: number;
  questions: any[];
  active: boolean;
  language: string;
  translations?: {
    [key: string]: {
      title: string;
      description: string;
      questions: any[];
    };
  };
  created_at: string;
  response_count?: number;
  target_countries?: string[];
}

interface SurveyResponse {
  id: string;
  user_id: string;
  responses: any;
  created_at: string;
  user_email?: string;
}

const Surveys: React.FC = () => {
  const { t } = useTranslation();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [surveyResponses, setSurveyResponses] = useState<SurveyResponse[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    points: 100,
    language: 'en',
    active: true,
    questions: [
      {
        id: 'q1',
        type: 'multiple_choice',
        text: '',
        options: [''],
        required: true
      }
    ],
    translations: {
      fr: { title: '', description: '', questions: [] },
      es: { title: '', description: '', questions: [] },
      pt: { title: '', description: '', questions: [] }
    },
    target_countries: [] as string[]
  });

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'es', label: 'Español' },
    { value: 'pt', label: 'Português' }
  ];

  const countryOptions = getCountryTranslations('en').map(country => ({
    value: country.value,
    label: country.label
  }));

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      
      // Get surveys
      const { data: surveysData, error: surveysError } = await supabase
        .from('surveys')
        .select('*')
        .order('created_at', { ascending: false });

      if (surveysError) throw surveysError;

      // Get response counts for each survey
      const surveysWithCounts = await Promise.all(
        surveysData.map(async (survey) => {
          const { count, error: countError } = await supabase
            .from('survey_responses')
            .select('*', { count: 'exact', head: true })
            .eq('survey_id', survey.id);

          if (countError) throw countError;

          return {
            ...survey,
            response_count: count || 0
          };
        })
      );

      setSurveys(surveysWithCounts);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error(t('errorFetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyResponses = async (surveyId: string) => {
    try {
      setLoadingResponses(true);
      
      // First get the survey responses
      const { data: responses, error: responsesError } = await supabase
        .from('survey_responses')
        .select('id, user_id, responses, created_at')
        .eq('survey_id', surveyId)
        .order('created_at', { ascending: false });

      if (responsesError) throw responsesError;

      if (!responses) {
        setSurveyResponses([]);
        return;
      }

      // Then fetch the corresponding user emails from profiles
      const responsesWithEmails = await Promise.all(
        responses.map(async (response) => {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('email')
            .eq('id', response.user_id)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
            return {
              ...response,
              user_email: t('anonymous')
            };
          }

          return {
            ...response,
            user_email: profileData?.email || t('anonymous')
          };
        })
      );

      setSurveyResponses(responsesWithEmails);
    } catch (error) {
      console.error('Error fetching survey responses:', error);
      toast.error(t('errorFetchingData'));
    } finally {
      setLoadingResponses(false);
    }
  };

  const downloadResponses = (survey: Survey) => {
    try {
      // Prepare CSV headers
      const headers = ['User Email', 'Date'];
      survey.questions.forEach(question => {
        headers.push(question.text);
      });

      // Prepare CSV rows
      const rows = surveyResponses.map(response => {
        const row = [
          response.user_email || 'Anonymous',
          new Date(response.created_at).toLocaleDateString()
        ];

        survey.questions.forEach(question => {
          const answer = response.responses[question.id];
          row.push(Array.isArray(answer) ? answer.join(', ') : answer?.toString() || '');
        });

        return row;
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `survey_responses_${survey.id}.csv`;
      link.click();
    } catch (error) {
      console.error('Error downloading responses:', error);
      toast.error(t('errorDownloadingResponses'));
    }
  };

  const handleCreateSurvey = async () => {
    try {
      const { error } = await supabase
        .from('surveys')
        .insert([{
          ...formData,
          target_countries: formData.target_countries.length > 0 ? formData.target_countries : null
        }]);

      if (error) throw error;

      toast.success(t('surveyCreated'));
      setShowCreateModal(false);
      fetchSurveys();
    } catch (error) {
      console.error('Error creating survey:', error);
      toast.error(t('errorCreatingSurvey'));
    }
  };

  const handleUpdateSurvey = async () => {
    if (!selectedSurvey) return;

    try {
      const { error } = await supabase
        .from('surveys')
        .update({
          ...formData,
          target_countries: formData.target_countries.length > 0 ? formData.target_countries : null
        })
        .eq('id', selectedSurvey.id);

      if (error) throw error;

      toast.success(t('surveyUpdated'));
      setShowEditModal(false);
      fetchSurveys();
    } catch (error) {
      console.error('Error updating survey:', error);
      toast.error(t('errorUpdatingSurvey'));
    }
  };

  const handleDeleteSurvey = async (id: string) => {
    try {
      const { error } = await supabase
        .from('surveys')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success(t('surveyDeleted'));
      fetchSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
      toast.error(t('errorDeletingSurvey'));
    }
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          id: `q${prev.questions.length + 1}`,
          type: 'multiple_choice',
          text: '',
          options: [''],
          required: true
        }
      ]
    }));
  };

  const removeQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const addOption = (questionIndex: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex ? { ...q, options: [...q.options, ''] } : q
      )
    }));
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex ? { 
          ...q, 
          options: q.options.filter((_, j) => j !== optionIndex) 
        } : q
      )
    }));
  };

  const updateOption = (questionIndex: number, optionIndex: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex ? {
          ...q,
          options: q.options.map((opt, j) => j === optionIndex ? value : opt)
        } : q
      )
    }));
  };

  const handleTranslationChange = (lang: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };

  const copyQuestionsToTranslation = (lang: string) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          questions: JSON.parse(JSON.stringify(prev.questions))
        }
      }
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const renderTranslationFields = (lang: string) => (
    <div key={lang} className="mt-4 p-4 bg-gray-50 rounded-lg">
      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
        <Globe className="h-4 w-4 mr-1" />
        {languageOptions.find(opt => opt.value === lang)?.label}
      </h4>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('title')}
          </label>
          <input
            type="text"
            value={formData.translations[lang].title}
            onChange={(e) => handleTranslationChange(lang, 'title', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('description')}
          </label>
          <textarea
            value={formData.translations[lang].description}
            onChange={(e) => handleTranslationChange(lang, 'description', e.target.value)}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('questions')}
            </label>
            <button
              type="button"
              onClick={() => copyQuestionsToTranslation(lang)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {t('copyQuestionsStructure')}
            </button>
          </div>
          
          {formData.translations[lang].questions.map((question: any, qIndex: number) => (
            <div key={qIndex} className="border rounded-md p-4 mb-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-medium text-gray-900">
                  {t('question')} {qIndex + 1}
                </h4>
              </div>

              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={question.text}
                    onChange={(e) => {
                      const newQuestions = [...formData.translations[lang].questions];
                      newQuestions[qIndex] = { ...question, text: e.target.value };
                      handleTranslationChange(lang, 'questions', newQuestions);
                    }}
                    placeholder={t('questionText')}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                {question.type === 'multiple_choice' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('options')}
                    </label>
                    {question.options.map((option: string, oIndex: number) => (
                      <div key={oIndex} className="flex items-center space-x-2 mb-2">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newQuestions = [...formData.translations[lang].questions];
                            newQuestions[qIndex].options[oIndex] = e.target.value;
                            handleTranslationChange(lang, 'questions', newQuestions);
                          }}
                          placeholder={`${t('option')} ${oIndex + 1}`}
                          className="flex-1 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout title={t('surveyManagement')}>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">{t('surveys')}</h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('createSurvey')}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : surveys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('noSurveys')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('title')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('language')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('points')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('targetCountries')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('responses')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {surveys.map((survey) => (
                  <tr key={survey.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{survey.title}</div>
                      <div className="text-sm text-gray-500">{survey.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Globe className="h-4 w-4 text-gray-500 mr-1" />
                        <span className="text-sm text-gray-900 uppercase">{survey.language}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{survey.points}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {survey.target_countries && survey.target_countries.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {survey.target_countries.map(code => (
                              <span key={code} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {code}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">{t('allCountries')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        survey.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {survey.active ? t('active') : t('inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{survey.response_count || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedSurvey(survey);
                          fetchSurveyResponses(survey.id);
                          setShowResponsesModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title={t('viewResponses')}
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedSurvey(survey);
                          setFormData({
                            title: survey.title,
                            description: survey.description,
                            points: survey.points,
                            language: survey.language,
                            active: survey.active,
                            questions: survey.questions,
                            translations: survey.translations || {
                              fr: { title: '', description: '', questions: [] },
                              es: { title: '', description: '', questions: [] },
                              pt: { title: '', description: '', questions: [] }
                            },
                            target_countries: survey.target_countries || []
                          });
                          setShowEditModal(true);
                        }}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                        title={t('edit')}
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteSurvey(survey.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('delete')}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Responses Modal */}
      {showResponsesModal && selectedSurvey && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {t('surveyResponses')} - {selectedSurvey.title}
                      </h3>
                      <button
                        onClick={() => downloadResponses(selectedSurvey)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Download className="h-5 w-5 mr-2" />
                        {t('downloadCSV')}
                      </button>
                    </div>

                    {loadingResponses ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : surveyResponses.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {t('noResponses')}
                      </div>
                    ) : (
                      <div className="mt-4 max-h-96 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('user')}
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('date')}
                              </th>
                              {selectedSurvey.questions.map((question, index) => (
                                <th key={index} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {question.text}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {surveyResponses.map((response) => (
                              <tr key={response.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {response.user_email || t('anonymous')}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatDate(response.created_at)}
                                </td>
                                {selectedSurvey.questions.map((question, index) => (
                                  <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {Array.isArray(response.responses[question.id])
                                      ? response.responses[question.id].join(', ')
                                      : response.responses[question.id]?.toString() || '-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowResponsesModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {showCreateModal ? t('createSurvey') : t('editSurvey')}
                    </h3>
                    <div className="mt-4">
                      <form>
                        <div className="mb-4">
                          <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('primaryLanguage')}
                          </label>
                          <Select
                            id="language"
                            options={languageOptions}
                            value={languageOptions.find(opt => opt.value === formData.language)}
                            onChange={(option) => setFormData(prev => ({ ...prev, language: option?.value || 'en' }))}
                            className="basic-single"
                            classNamePrefix="select"
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('title')}
                          </label>
                          <input
                            type="text"
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('description')}
                          </label>
                          <textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          
                          <div>
                            <label htmlFor="points" className="block text-sm font-medium text-gray-700 mb-1">
                              {t('points')}
                            </label>
                            <input
                              type="number"
                              id="points"
                              value={formData.points}
                              onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('status')}
                            </label>
                            <div className="mt-2">
                              <label className="inline-flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.active}
                                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <span className="ml-2 text-sm text-gray-700">{t('active')}</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="mb-4">
                          <label htmlFor="target_countries" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('targetCountries')}
                          </label>
                          <Select
                            id="target_countries"
                            isMulti
                            options={countryOptions}
                            value={countryOptions.filter(option => 
                              formData.target_countries.includes(option.value)
                            )}
                            onChange={(selected) => {
                              setFormData({
                                ...formData,
                                target_countries: selected ? selected.map(option => option.value) : []
                              });
                            }}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            placeholder={t('selectCountries')}
                            isClearable={true}
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            {t('leaveEmptyForAllCountries')}
                          </p>
                        </div>

                        {/* Questions */}
                        <div className="mb-4">
                          <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('questions')}
                            </label>
                            <button
                              type="button"
                              onClick={addQuestion}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              {t('addQuestion')}
                            </button>
                          </div>

                          {formData.questions.map((question, qIndex) => (
                            <div key={qIndex} className="border rounded-md p-4 mb-4">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="text-sm font-medium text-gray-900">
                                  {t('question')} {qIndex + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => removeQuestion(qIndex)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="space-y-4">
                                <div>
                                  <input
                                    type="text"
                                    value={question.text}
                                    onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
                                    placeholder={t('questionText')}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                  />
                                </div>

                                <div>
                                  <select
                                    value={question.type}
                                    onChange={(e) => updateQuestion(qIndex, 'type', e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                  >
                                    <option value="multiple_choice">{t('multipleChoice')}</option>
                                    <option value="text">{t('text')}</option>
                                    <option value="rating">{t('rating')}</option>
                                    <option value="boolean">{t('boolean')}</option>
                                  </select>
                                </div>

                                {question.type === 'multiple_choice' && (
                                  <div>
                                    <div className="flex justify-between items-center mb-2">
                                      <label className="block text-sm font-medium text-gray-700">
                                        {t('options')}
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() => addOption(qIndex)}
                                        className="inline-flex items-center px-2 py-1 text-sm text-blue-600 hover:text-blue-800"
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        {t('addOption')}
                                      </button>
                                    </div>
                                    {question.options.map((option, oIndex) => (
                                      <div key={oIndex} className="flex items-center space-x-2 mb-2">
                                        <input
                                          type="text"
                                          value={option}
                                          onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                          placeholder={`${t('option')} ${oIndex + 1}`}
                                          className="flex-1 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => removeOption(qIndex, oIndex)}
                                          className="text-red-600 hover:text-red-900"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div>
                                  <label className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={question.required}
                                      onChange={(e) => updateQuestion(qIndex, 'required', e.target.checked)}
                                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{t('required')}</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Translations */}
                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <h4 className="text-lg font-medium text-gray-900 mb-4">{t('translations')}</h4>
                          {Object.keys(formData.translations)
                            .filter(lang => lang !== formData.language)
                            .map(lang => renderTranslationFields(lang))}
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={showCreateModal ? handleCreateSurvey : handleUpdateSurvey}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {showCreateModal ? t('create') : t('update')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Surveys;